import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Shield, ShieldCheck, ShieldAlert, Search,
  CheckCircle, Eye, EyeOff, Package, MapPin, Building, Radio, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const ADMIN_LEVELS = [
  { 
    value: 'full', 
    label: 'Administrateur complet', 
    description: 'Acces total a toutes les fonctionnalites',
    icon: ShieldCheck,
    color: 'text-green-600 bg-green-100',
    borderColor: 'border-green-300'
  },
  { 
    value: 'limited', 
    label: 'Administrateur limite', 
    description: 'Acces restreint (planning, RJ, formations)',
    icon: Shield,
    color: 'text-blue-600 bg-blue-100',
    borderColor: 'border-blue-300'
  },
  { 
    value: 'colis_prive', 
    label: 'Admin Colis Prive', 
    description: 'Acces limite au(x) site(s) Colis Prive assigne(s)',
    icon: Package,
    color: 'text-orange-600 bg-orange-100',
    borderColor: 'border-orange-300'
  }
];

const AdminManagement = ({ user }) => {
  const [admins, setAdmins] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAdmin, setExpandedAdmin] = useState(null);
  // Track pending site selections per admin
  const [pendingSites, setPendingSites] = useState({});
  const [operationsSaving, setOperationsSaving] = useState(null);

  const isSuperAdmin = user?.is_super_admin === true || user?.email === 'transport@dlv-express.com';
  const isAdminSuperAdmin = (admin) => admin?.is_super_admin === true || admin?.email === 'transport@dlv-express.com';

  const toggleOperationsManager = async (admin) => {
    const enabling = !admin.operations_manager;
    const adminName = admin.name || admin.email;
    const confirmed = window.confirm(enabling
      ? `Attribuer le poste « Responsable d'exploitation » à ${adminName} ?\n\nCe compte n'aura plus accès qu'aux plannings, missions et pointages des dispatchers (ni livreurs, ni salaires, ni clôtures).`
      : `Retirer le poste « Responsable d'exploitation » à ${adminName} ?\n\nSes droits administrateur précédents seront rétablis.`);
    if (!confirmed) return;
    setOperationsSaving(admin.user_id);
    try {
      const { data } = await axios.put(
        `${API}/internal-staff/operations-admins/${admin.user_id}`,
        { enabled: enabling },
        { withCredentials: true }
      );
      setAdmins(prev => prev.map(a => a.user_id === admin.user_id ? { ...a, operations_manager: data.operations_manager } : a));
      toast.success(enabling ? 'Poste Responsable d\'exploitation attribué' : 'Poste Responsable d\'exploitation retiré');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du poste');
    } finally {
      setOperationsSaving(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Layout user={user} currentModule="b2b">
        <div className="p-6 max-w-2xl mx-auto">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-8 text-center">
              <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-red-700 mb-2">Acces refuse</h2>
              <p className="text-red-600">Seul le compte administrateur principal peut gerer les acces.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, sitesRes] = await Promise.all([
        axios.get(`${API}/users?role=admin`, { withCredentials: true }),
        // Fetch ALL sites (not filtered by admin role) for assignment
        axios.get(`${API}/sites`, { withCredentials: true })
      ]);
      const adminUsers = (usersRes.data || []).filter(u => u.role === 'admin');
      setAdmins(adminUsers);
      setSites(sitesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Get CP sites only (for site assignment)
  const cpSites = sites.filter(s =>
    s.name?.toLowerCase().includes('colis') ||
    s.name?.toLowerCase().includes('priv') ||
    s.type === 'colis_prive'
  );

  const updateAdminLevel = async (adminId, newLevel, siteIds = []) => {
    setSaving(adminId);
    try {
      await axios.put(
        `${API}/users/${adminId}/role`,
        { role: 'admin', admin_level: newLevel, assigned_site_ids: siteIds },
        { withCredentials: true }
      );
      setAdmins(prev => prev.map(a =>
        a.user_id === adminId ? { ...a, admin_level: newLevel, assigned_site_ids: siteIds } : a
      ));
      toast.success('Niveau d\'acces mis a jour');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(null);
    }
  };

  const toggleSite = (adminId, siteId) => {
    setPendingSites(prev => {
      const current = prev[adminId] || [];
      const updated = current.includes(siteId)
        ? current.filter(id => id !== siteId)
        : [...current, siteId];
      return { ...prev, [adminId]: updated };
    });
  };

  const handleExpand = (adminId) => {
    if (expandedAdmin === adminId) {
      setExpandedAdmin(null);
    } else {
      setExpandedAdmin(adminId);
      // Initialize pending sites from admin's current assignment
      const admin = admins.find(a => a.user_id === adminId);
      setPendingSites(prev => ({
        ...prev,
        [adminId]: admin?.assigned_site_ids || []
      }));
    }
  };

  const handleSaveCPAdmin = (adminId) => {
    const siteIds = pendingSites[adminId] || [];
    if (siteIds.length === 0) {
      toast.error('Selectionnez au moins un site');
      return;
    }
    updateAdminLevel(adminId, 'colis_prive', siteIds);
  };

  const filteredAdmins = admins.filter(a => {
    const s = searchTerm.toLowerCase();
    return a.name?.toLowerCase().includes(s) || a.email?.toLowerCase().includes(s) ||
      a.first_name?.toLowerCase().includes(s) || a.last_name?.toLowerCase().includes(s);
  });

  const getLevelInfo = (level) => ADMIN_LEVELS.find(l => l.value === level) || ADMIN_LEVELS[0];

  const getSiteNames = (siteIds) => {
    if (!siteIds || siteIds.length === 0) return 'Aucun site assigne';
    return siteIds.map(id => {
      const site = sites.find(s => s.site_id === id);
      return site?.name || id;
    }).join(', ');
  };

  return (
    <Layout user={user} currentModule="b2b">
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-management-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-testid="page-title">
            <ShieldCheck className="w-7 h-7 text-blue-600" />
            Gestion des Administrateurs
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Assignez les niveaux d'acces et les sites a chaque administrateur</p>
        </div>

        {/* Poste Responsable d'exploitation */}
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="operations-manager-info-card">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Radio className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-sm text-slate-900">Poste « Responsable d'exploitation » (dispatchers uniquement)</p>
                <p className="text-xs text-slate-600 mt-1">
                  Attribuable ou retirable sur chaque fiche ci-dessous. Un administrateur titulaire ne peut que gérer les plannings des dispatchers, accepter leurs missions et traiter leurs pointages. Aucun accès aux livreurs, aux salaires ni aux clôtures. Le retrait rétablit ses droits précédents.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button asChild variant="outline" size="sm" data-testid="operations-workspace-link">
                <Link to="/operations/planning"><ExternalLink className="w-4 h-4 mr-1" /> Espace exploitation</Link>
              </Button>
              <Button asChild variant="outline" size="sm" data-testid="operations-access-link">
                <Link to="/operations/access"><Shield className="w-4 h-4 mr-1" /> Onglet Responsables</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="bg-gradient-to-r from-slate-50 to-blue-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Types d'acces</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {ADMIN_LEVELS.map(level => {
                const Icon = level.icon;
                return (
                  <div key={level.value} className={`p-3 rounded-lg border-2 ${level.borderColor} bg-white`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${level.color.split(' ')[0]}`} />
                      <span className="font-semibold text-sm">{level.label}</span>
                    </div>
                    <p className="text-xs text-slate-500">{level.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input placeholder="Rechercher un administrateur..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
        </div>

        {/* Admin List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAdmins.map(admin => {
              const levelInfo = getLevelInfo(admin.admin_level);
              const LevelIcon = levelInfo.icon;
              const isCurrentUser = admin.user_id === user?.user_id;
              const isExpanded = expandedAdmin === admin.user_id;
              const adminName = admin.name || `${admin.first_name || ''} ${admin.last_name || ''}`.trim() || 'Admin';

              return (
                <Card key={admin.user_id} className={`transition-all ${isCurrentUser ? 'border-blue-200 bg-blue-50/30' : ''}`} data-testid={`admin-card-${admin.user_id}`}>
                  <CardContent className="p-5">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center ${levelInfo.color}`}>
                          <LevelIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{adminName}</h3>
                            {isCurrentUser && <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Vous</span>}
                          </div>
                          <p className="text-xs text-slate-400">{admin.email}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${levelInfo.color}`} data-testid={`level-badge-${admin.user_id}`}>
                              {levelInfo.label}
                            </span>
                            {admin.operations_manager === true && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800" data-testid={`operations-badge-${admin.user_id}`}>
                                <Radio className="w-3 h-3" />
                                Responsable d'exploitation · dispatchers uniquement
                              </span>
                            )}
                            {admin.admin_level === 'colis_prive' && admin.assigned_site_ids?.length > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600" data-testid={`site-badge-${admin.user_id}`}>
                                <MapPin className="w-3 h-3" />
                                {getSiteNames(admin.assigned_site_ids)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {!isCurrentUser && (
                        <div className="flex flex-wrap gap-2">
                          {!isAdminSuperAdmin(admin) && (
                            <Button
                              variant={admin.operations_manager ? 'outline' : 'default'}
                              size="sm"
                              className={admin.operations_manager ? 'border-emerald-600 text-emerald-800' : 'bg-emerald-700 hover:bg-emerald-800'}
                              disabled={operationsSaving === admin.user_id}
                              onClick={() => toggleOperationsManager(admin)}
                              data-testid={`operations-toggle-${admin.user_id}`}
                            >
                              <Radio className="w-4 h-4 mr-1" />
                              {admin.operations_manager ? 'Retirer le poste Resp. exploitation' : 'Attribuer le poste Resp. exploitation'}
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => handleExpand(admin.user_id)} data-testid={`toggle-${admin.user_id}`}>
                            {isExpanded ? <><EyeOff className="w-4 h-4 mr-1" /> Masquer</> : <><Eye className="w-4 h-4 mr-1" /> Modifier acces</>}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Expanded: Level + Site Selection */}
                    {isExpanded && !isCurrentUser && (
                      <div className="mt-5 pt-5 border-t space-y-4">
                        <h4 className="font-medium text-slate-700 text-sm">Niveau d'acces :</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {ADMIN_LEVELS.map(level => {
                            const Icon = level.icon;
                            const isSelected = admin.admin_level === level.value || (!admin.admin_level && level.value === 'full');
                            return (
                              <button
                                key={level.value}
                                onClick={() => {
                                  if (level.value === 'colis_prive') {
                                    // Don't save yet, need to select sites first
                                    setAdmins(prev => prev.map(a => a.user_id === admin.user_id ? { ...a, admin_level: 'colis_prive' } : a));
                                  } else {
                                    updateAdminLevel(admin.user_id, level.value, []);
                                  }
                                }}
                                disabled={saving === admin.user_id}
                                className={`p-3 rounded-lg border-2 text-left transition-all cursor-pointer ${
                                  isSelected ? `${level.borderColor} ${level.color}` : 'border-slate-200 hover:border-slate-300'
                                }`}
                                data-testid={`level-select-${level.value}-${admin.user_id}`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <Icon className={`w-4 h-4 ${isSelected ? level.color.split(' ')[0] : 'text-slate-400'}`} />
                                    <span className={`font-medium text-sm ${isSelected ? level.color.split(' ')[0] : 'text-slate-700'}`}>{level.label}</span>
                                  </div>
                                  {isSelected && <CheckCircle className={`w-4 h-4 ${level.color.split(' ')[0]}`} />}
                                </div>
                                <p className="text-xs text-slate-500">{level.description}</p>
                              </button>
                            );
                          })}
                        </div>

                        {/* Site Selection for Colis Prive admins */}
                        {(admin.admin_level === 'colis_prive') && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3" data-testid={`site-selector-${admin.user_id}`}>
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-orange-600" />
                              <h4 className="font-semibold text-orange-800 text-sm">Assigner le(s) site(s) Colis Prive :</h4>
                            </div>
                            <p className="text-xs text-orange-600">Cet admin ne verra que les donnees des sites selectionnes ci-dessous.</p>
                            
                            <div className="space-y-2">
                              {cpSites.length === 0 ? (
                                <p className="text-sm text-slate-500">Aucun site Colis Prive trouve</p>
                              ) : (
                                cpSites.map(site => {
                                  const isChecked = (pendingSites[admin.user_id] || []).includes(site.site_id);
                                  return (
                                    <label key={site.site_id} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      isChecked ? 'border-orange-400 bg-orange-100' : 'border-slate-200 bg-white hover:border-slate-300'
                                    }`} data-testid={`site-option-${site.site_id}`}>
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleSite(admin.user_id, site.site_id)}
                                        className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                                      />
                                      <div className="flex items-center gap-2">
                                        <MapPin className={`w-4 h-4 ${isChecked ? 'text-orange-600' : 'text-slate-400'}`} />
                                        <span className={`font-medium text-sm ${isChecked ? 'text-orange-800' : 'text-slate-700'}`}>{site.name}</span>
                                      </div>
                                    </label>
                                  );
                                })
                              )}
                            </div>

                            <Button
                              onClick={() => handleSaveCPAdmin(admin.user_id)}
                              disabled={saving === admin.user_id || (pendingSites[admin.user_id] || []).length === 0}
                              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                              data-testid={`save-sites-${admin.user_id}`}
                            >
                              {saving === admin.user_id ? 'Enregistrement...' : `Enregistrer (${(pendingSites[admin.user_id] || []).length} site(s) selectionne(s))`}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Info */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-amber-800 text-sm">Important</h4>
                <p className="text-xs text-amber-700 mt-1">
                  Les modifications sont immediates. L'administrateur verra ses nouveaux droits des sa prochaine connexion.
                  Chaque admin Colis Prive ne peut voir QUE les donnees de son/ses site(s) assigne(s).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminManagement;
